import React, { useEffect, useRef, useState } from 'react'
import { InputNumber } from 'antd'
import { CMYKColorSelector, CMYKColorContainer } from './styles'

const hexToRgb = (hex = '#000000') => {
  const v = parseInt(typeof(hex) === 'string' ? hex.replace('#', '') : hex[0].replace('#', ''), 16)
  return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255, a: 1 }
}

const rgbToHex = (r, g, b) =>
  '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')

const rgbToCmyk = (r, g, b) => {
  const r1 = r / 255, g1 = g / 255, b1 = b / 255
  const k = 1 - Math.max(r1, g1, b1)
  if (k === 1) return { c: 0, m: 0, y: 0, k: 100 }

  return {
    c: Math.round(((1 - r1 - k) / (1 - k)) * 100),
    m: Math.round(((1 - g1 - k) / (1 - k)) * 100),
    y: Math.round(((1 - b1 - k) / (1 - k)) * 100),
    k: Math.round(k * 100),
  }
}

const cmykToRgb = (c, m, y, k) => ({
  r: Math.round(255 * (1 - c / 100) * (1 - k / 100)),
  g: Math.round(255 * (1 - m / 100) * (1 - k / 100)),
  b: Math.round(255 * (1 - y / 100) * (1 - k / 100)),
  a: 1,
})

export const CMYKSelector = ({ hex, onChange }) => {
  const [cmyk, setCmyk] = useState({ c: 0, m: 0, y: 0, k: 0 })
  const [localHex, setLocalHex] = useState(hex || '')
  const sourceRef = useRef('hex')

  useEffect(() => {
    const nextHex = hex ? typeof(hex) === 'string' ? hex.substring(0, 7) : hex[0].substring(0, 7) : ''
    if (nextHex !== localHex) {
      setLocalHex(nextHex)
    }
  }, [hex])

  useEffect(() => {
    if (sourceRef.current === 'cmyk') {
      sourceRef.current = 'hex'
      return
    }

    if (!localHex) return

    const rgb = hexToRgb(localHex)
    setCmyk(rgbToCmyk(rgb.r, rgb.g, rgb.b))
  }, [localHex])

  const update = (key, value, e) => {
    if (value === null) return
    let intValue = parseInt(value, 10)
    if (Number.isNaN(intValue)) intValue = 0

    const next = {
      ...cmyk,
      [key]: Math.min(100, Math.max(0, intValue)),
    }

    setCmyk(next)
    if (e.key === 'Enter') {
      e.target.blur()
    }

    const rgb = cmykToRgb(next.c, next.m, next.y, next.k)
    const hexValue = rgbToHex(rgb.r, rgb.g, rgb.b)
    sourceRef.current = 'cmyk'

    onChange(
      {
        rgb,
        hex: hexValue,
        source: 'cmyk',
      },
      e
    )
  }

  return (
    <CMYKColorContainer>
      {['c', 'm', 'y', 'k'].map((key) => (
          <CMYKColorSelector key={key}>
            <InputNumber
              min={0}
              max={100}
              value={cmyk[key]}
              onKeyUp={(e) => update(key, e.target.value, e)}
              style={{ width: '100%' }}
              placeholder={key.toUpperCase()}
            />
            {key.toUpperCase()}
          </CMYKColorSelector>
      ))}
    </CMYKColorContainer>
  )
}
